import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { tasksApi, teamApi, reminderConfigApi, taskAttachmentsApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { REMINDER_DAY_CHOICES } from "../../lib/taskHelpers";
import { defaultRecurringValue, resolveRecurringReminder } from "../../lib/reminderUtils";
import { MultiAssigneeSelect } from "./MultiAssigneeSelect";
import { CompanyCombobox } from "./CompanyCombobox";
import { RecurringReminderFields } from "./RecurringReminderFields";
import { PendingAttachments } from "./PendingAttachments";
import { PendingSubtasks } from "./PendingSubtasks";
import { confirmDialog } from "../../lib/confirm";
import { kirliMi, KIRLI_UYARI } from "../../lib/formDirty";
import { useFormDraft } from "../../lib/useFormDraft";
import { DraftRestoreBar } from "../DraftRestoreBar";
import CategorySelect from "./CategorySelect";
import { SerialDateFields } from "./SerialDateFields";

// Detaylı/Kolay ile aynı "Yeni Görev Ekle" formu. testPrefix ile
// her temanın kendi data-testid'leri korunur (kolay-add / prof-add).
export const AddTaskModal = ({ cats, onClose, onCreated, testPrefix = "add", taslakOtoYukle = false, onTaslakGeriYukle }) => {
  const { isTeamView, user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assigneeName, setAssigneeName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAutoFilled, setCompanyAutoFilled] = useState(false);
  const [assigneeUserIds, setAssigneeUserIds] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newReminderDays, setNewReminderDays] = useState(null);
  const [newReminderDisabled, setNewReminderDisabled] = useState(false);
  const [newReminder, setNewReminder] = useState(defaultRecurringValue());
  const [reminderConfig, setReminderConfig] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [assignSerial, setAssignSerial] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [saving, setSaving] = useState(false);
  // FAZ D — taslak alt görevler: imperative erişim (yazarken re-render olmasın).
  const subsRef = useRef(null);
  // FAZ F — çift gönderim kilidi (senkron; state gecikmesinden etkilenmez).
  const kaydetKilidi = useRef(false);
  const [failedSubtasks, setFailedSubtasks] = useState(null);
  const createdIdRef = useRef(null);

  // Alt görev ağacını kaydet. Tek PATCH ile tüm hiyerarşi + sıra korunur.
  const saveSubtasks = useCallback(async (taskId) => {
    const derlenmis = subsRef.current?.derle?.();
    if (!derlenmis || !derlenmis.agac.length) {
      if (derlenmis?.atlanan) {
        toast(`${derlenmis.atlanan} boş alt görev atlandı`);
      }
      return true;
    }
    const { agac, atlanan, tasinan } = derlenmis;
    try {
      await tasksApi.setSubtasks(taskId, agac);
      setFailedSubtasks(null);
      if (atlanan > 0) {
        toast(
          tasinan > 0
            ? `${atlanan} boş alt görev atlandı, altındaki ${tasinan} öğe üst seviyeye taşındı`
            : `${atlanan} boş alt görev atlandı`,
        );
      }
      return true;
    } catch {
      // KISMİ BAŞARISIZLIK: görev oluştu ama alt görevler gitmedi. Kullanıcının
      // yazdığı veri bellekte KALIR; hangi satırların gitmediği listelenir.
      const isimler = [];
      const gez = (l) => l.forEach((n) => { isimler.push(n.text); gez(n.children || []); });
      gez(agac);
      setFailedSubtasks(isimler);
      toast.error("Alt görevler kaydedilemedi — 'TEKRAR DENE' ile yeniden gönderin");
      return false;
    }
  }, []);

  const retrySubtasks = useCallback(async () => {
    if (!createdIdRef.current) return;
    setSaving(true);
    const ok = await saveSubtasks(createdIdRef.current);
    setSaving(false);
    if (ok) {
      toast.success("Alt görevler kaydedildi");
      onCreated?.();
      onClose?.();
    }
  }, [saveSubtasks, onCreated, onClose]);

  // FAZ F — form içeriği kirli mi? (açılış anlık görüntüsüne göre)
  const anlikForm = useCallback(() => ({
    title, description, startDate, dueDate, assigneeName, companyName,
    assigneeUserIds, newCategoryId, newReminderDays, newReminderDisabled,
    newReminder, assignSerial, showDate,
    pendingFiles: pendingFiles.map((f) => f.name),
  }), [title, description, startDate, dueDate, assigneeName, companyName,
    assigneeUserIds, newCategoryId, newReminderDays, newReminderDisabled,
    newReminder, assignSerial, showDate, pendingFiles]);
  const baslangicRef = useRef(null);
  useEffect(() => { baslangicRef.current = anlikForm(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // FAZ G — taslak kurtarma (yazma yalnız çıkışta / sekme kapanışında).
  const kirliMiFn = useCallback(
    () => kirliMi(anlikForm(), baslangicRef.current) || !!subsRef.current?.isimliVarMi?.(),
    [anlikForm],
  );
  const taslakTopla = useCallback(() => ({
    alanlar: anlikForm(),
    dosyaAdlari: pendingFiles.map((f) => f.name),
    altGorevler: subsRef.current?.taslakAl?.() || [],
  }), [anlikForm, pendingFiles]);
  const { teklif, setTeklif, yaz: taslakYaz, sil: taslakSilBu } = useFormDraft({
    userId: user?.id, formKey: `add-task:${testPrefix}`, topla: taslakTopla, kirli: kirliMiFn,
  });

  const geriYukle = useCallback((veri) => {
    const a = veri?.alanlar || {};
    setTitle(a.title || "");
    setDescription(a.description || "");
    setStartDate(a.startDate || "");
    setDueDate(a.dueDate || "");
    setAssigneeName(a.assigneeName || "");
    setCompanyName(a.companyName || "");
    setAssigneeUserIds(a.assigneeUserIds || []);
    setNewCategoryId(a.newCategoryId || "");
    setNewReminderDays(a.newReminderDays ?? null);
    setNewReminderDisabled(!!a.newReminderDisabled);
    if (a.newReminder) setNewReminder(a.newReminder);
    setAssignSerial(!!a.assignSerial);
    setShowDate(!!a.showDate);
    subsRef.current?.taslakYukle?.(veri?.altGorevler || []);
    setTeklif(null);
    const dosyalar = veri?.dosyaAdlari || [];
    if (dosyalar.length) {
      toast(`${dosyalar.length} dosya geri yüklenemedi, tekrar seçmeniz gerekiyor`, {
        description: dosyalar.join(", "), duration: 8000,
      });
    }
    toast.success("Taslak geri yüklendi");
  }, [setTeklif]);

  useEffect(() => {
    if (taslakOtoYukle && teklif?.veri) geriYukle(teklif.veri);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taslakOtoYukle, teklif]);

  // Kapatma uyarısı — X / backdrop / Escape yollarının HEPSİ buradan geçer.
  const tryClose = useCallback(async () => {
    if (kirliMiFn()) {
      const ok = await confirmDialog(KIRLI_UYARI);
      if (!ok) return;
      if (taslakYaz()) {
        toast("Taslak kaydedildi", {
          action: { label: "GERİ YÜKLE", onClick: () => onTaslakGeriYukle?.() },
          duration: 8000,
        });
      }
    }
    onClose?.();
  }, [kirliMiFn, taslakYaz, onTaslakGeriYukle, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); tryClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tryClose]);

  useEffect(() => {
    if (isTeamView) teamApi.members().then(setTeamMembers).catch(() => setTeamMembers([]));
    reminderConfigApi.get().then(setReminderConfig).catch(() => setReminderConfig(null));
  }, [isTeamView]);

  const submit = async () => {
    if (kaydetKilidi.current) return;
    if (!title.trim()) { toast.error("Başlık gerekli"); return; }
    if (startDate && dueDate && new Date(startDate) > new Date(dueDate)) {
      toast.error("Başlangıç tarihi bitiş tarihinden sonra olamaz");
      return;
    }
    setSaving(true);
    kaydetKilidi.current = true;
    try {
      const ids = assigneeUserIds;
      const others = ids.filter((x) => x !== user?.id);
      const includesSelf = user?.id ? ids.includes(user.id) : false;
      const extras = {
        assignee_name: assigneeName.trim() || null,
        company_name: companyName.trim() || null,
      };
      if (ids.length === 0 || (ids.length === 1 && includesSelf)) {
        // kişisel görev
      } else if (others.length === 1 && !includesSelf) {
        extras.assignee_user_id = others[0];
      } else {
        extras.assignee_user_ids = ids;
      }
      if (newCategoryId) extras.category_id = newCategoryId;
      if (assignSerial) extras.assign_serial = true;
      if (showDate) extras.show_created_date = true;
      if (startDate) extras.start_date = new Date(startDate).toISOString();
      if (newReminderDisabled) extras.reminder_disabled = true;
      else if (newReminderDays != null) extras.reminder_days = newReminderDays;
      const rr = resolveRecurringReminder(newReminder);
      if (rr.error) { toast.error(rr.error); setSaving(false); kaydetKilidi.current = false; return; }
      if (rr.reminder_at) {
        extras.reminder_interval_min = rr.reminder_interval_min;
        extras.reminder_repeat_total = rr.reminder_repeat_total;
        extras.reminder_repeat_left = rr.reminder_repeat_left;
      }
      const created = await tasksApi.create(
        title.trim(),
        description.trim(),
        dueDate ? new Date(dueDate).toISOString() : null,
        rr.reminder_at || null,
        extras,
      );
      if (created?.id && pendingFiles.length) {
        let okCount = 0;
        for (const f of pendingFiles) {
          try { await taskAttachmentsApi.upload(created.id, f); okCount += 1; }
          catch { toast.error(`Dosya yüklenemedi: ${f.name}`); }
        }
        if (okCount) toast.success(`${okCount} dosya göreve eklendi`);
      }
      toast.success("Görev eklendi");
      taslakSilBu();
      // Alt görevler: görev ve dosyalardan SONRA, aynı görev id'siyle.
      if (created?.id) {
        createdIdRef.current = created.id;
        const subsOk = await saveSubtasks(created.id);
        if (!subsOk) { onCreated?.(); setSaving(false); kaydetKilidi.current = false; return; }
      }
      onCreated?.();
      onClose?.();
    } catch {
      toast.error("Görev eklenemedi");
    } finally {
      setSaving(false);
      kaydetKilidi.current = false;
    }
  };

  const defaultReminderLabel = reminderConfig?.effective
    ? `⏱ Uyarı: Varsayılan (${reminderConfig.effective} gün)`
    : "⏱ Uyarı: Varsayılan";

  const inputCls = "w-full px-3 py-2.5 rounded-lg bg-sertex-surface/60 border border-sertex-cyan/25 text-sertex-text font-mono text-sm placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none";

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={tryClose} data-testid={`${testPrefix}-overlay`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-panel border border-sertex-cyan/40 rounded-xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-sertex"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testPrefix}-modal`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="display-text text-sertex-cyan neon-glow flex items-center gap-2"><Plus className="h-4 w-4" /> YENİ GÖREV</div>
          <button onClick={tryClose} data-testid={`${testPrefix}-close`} className="text-sertex-textMuted hover:text-sertex-cyan"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          {teklif && !taslakOtoYukle && (
            <DraftRestoreBar
              at={teklif.at}
              onRestore={() => geriYukle(teklif.veri)}
              onIgnore={() => { taslakSilBu(); toast("Taslak yoksayıldı"); }}
              testid={`${testPrefix}-draft-bar`}
            />
          )}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            data-testid={`${testPrefix}-title`}
            placeholder="Görev başlığı"
            className={inputCls}
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid={`${testPrefix}-desc`}            rows={2}
            placeholder="Açıklama (opsiyonel)"
            className={`${inputCls} resize-none`}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">BAŞLANGIÇ</div>
              <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid={`${testPrefix}-start`} className={inputCls} />
            </div>
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">BİTİŞ</div>
              <input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid={`${testPrefix}-due`} className={inputCls} />
            </div>
          </div>
          {isTeamView && (
            <div className="space-y-2">
              {teamMembers.length > 0 ? (
                <MultiAssigneeSelect
                  members={teamMembers}
                  selfUser={user ? { id: user.id, username: user.username } : null}
                  selectedIds={assigneeUserIds}
                  companyFilter={companyName}
                  onChange={(newIds) => {
                    setAssigneeUserIds(newIds);
                    const others = newIds.filter((x) => x !== user?.id);
                    if (companyAutoFilled || !companyName.trim()) {
                      if (others.length === 1) {
                        setCompanyName(teamMembers.find((m) => m.id === others[0])?.company_name || "");
                        setCompanyAutoFilled(true);
                      } else if (newIds.length === 0) {
                        setCompanyName("");
                        setCompanyAutoFilled(true);
                      }
                    }
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={assigneeName}
                  onChange={(e) => setAssigneeName(e.target.value)}
                  placeholder="Görev sahibi (opsiyonel)"
                  data-testid={`${testPrefix}-assignee`}
                  className={inputCls}
                />
              )}
              <CompanyCombobox
                value={companyName}
                onChange={setCompanyName}
                onManualEdit={(isManual) => setCompanyAutoFilled(!isManual)}
                options={teamMembers.map((m) => m.company_name).filter(Boolean)}
                placeholder="Şirket (opsiyonel)"
                testId={`${testPrefix}-company`}
              />
            </div>
          )}
          <CategorySelect
            categories={cats}
            value={newCategoryId}
            onChange={setNewCategoryId}
            testId={`${testPrefix}-category`}
          />
          {/* FAZ D — taslak alt görevler (görev oluşunca hiyerarşiyle kaydedilir) */}
          <PendingSubtasks apiRef={subsRef} />
          {failedSubtasks && (
            <div
              data-testid="pending-subtask-failed"
              className="rounded border border-rose-400/50 bg-rose-500/10 px-2 py-1.5 space-y-1"
            >
              <div className="hud-text text-rose-300">
                {failedSubtasks.length} alt görev oluşturulamadı — verileriniz duruyor:
              </div>
              <div className="text-[11px] font-mono text-rose-200/90 max-h-20 overflow-y-auto">
                {failedSubtasks.map((t, i) => (
                  <div key={i} className="truncate">• {t}</div>
                ))}
              </div>
              <button
                type="button"
                onClick={retrySubtasks}
                disabled={saving}
                data-testid="pending-subtask-retry"
                className="px-2 py-0.5 rounded border border-rose-400/50 text-rose-200 hover:bg-rose-500/20 hud-text transition-colors disabled:opacity-50"
              >
                TEKRAR DENE
              </button>
            </div>
          )}
          <select
            value={newReminderDisabled ? "__off__" : (newReminderDays == null ? "" : String(newReminderDays))}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__off__") { setNewReminderDisabled(true); setNewReminderDays(null); }
              else if (v === "") { setNewReminderDisabled(false); setNewReminderDays(null); }
              else { setNewReminderDisabled(false); setNewReminderDays(parseInt(v, 10)); }
            }}
            data-testid={`${testPrefix}-reminder-days`}
            className={inputCls}
          >
            <option value="">{defaultReminderLabel}</option>
            {REMINDER_DAY_CHOICES.map((d) => (
              <option key={d} value={d}>{`⏱ Uyarı: ${d} gün önce`}</option>
            ))}
            <option value="__off__">🚫 Bu görev için hatırlatıcı kapalı</option>
          </select>
          <RecurringReminderFields value={newReminder} onChange={setNewReminder} testPrefix={`${testPrefix}-reminder`} />
          <SerialDateFields
            testPrefix={testPrefix}
            assignSerial={assignSerial}
            setAssignSerial={setAssignSerial}
            showDate={showDate}
            setShowDate={setShowDate}
          />
          <PendingAttachments files={pendingFiles} onChange={setPendingFiles} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={tryClose} data-testid={`${testPrefix}-cancel`} className="px-4 py-2 rounded-lg border border-sertex-textMuted/30 text-sertex-textMuted hover:text-sertex-text font-mono text-sm">İPTAL</button>
          <button onClick={submit} disabled={saving} data-testid={`${testPrefix}-submit`} className="px-4 py-2 rounded-lg bg-sertex-cyan/15 border border-sertex-cyan text-sertex-cyan hover:bg-sertex-cyan/25 font-mono text-sm neon-glow disabled:opacity-50">
            {saving ? "EKLENİYOR..." : "EKLE"}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

export default AddTaskModal;
